import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Container
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Article as PostsIcon,
  Work as JobsIcon,
  People as DoctorsIcon,
  Business as DepartmentsIcon,
  Group as BoardIcon,
  PeopleAlt as TeamIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Business as HospitalInfoIcon,
  DesignServices as ServicesIcon,
  Description as ApplicationsIcon,
  MarkEmailUnread as ContactMessagesIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Home as HomeContentIcon
} from '@mui/icons-material';
import apiClient from '../../services/api';
import { showGlobalLoader } from '../../globalLoader';
import AdminLogin from './AdminLogin';

const drawerWidth = 240;

const AdminLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => {
    const user = localStorage.getItem('currentUser');
    return user ? JSON.parse(user) : null;
  });
  const [currentUserId, setCurrentUserId] = useState(() => localStorage.getItem('currentUserId') || '');
  const [perms, setPerms] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!currentUser);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    showGlobalLoader(false);
  }, []);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin', perm: 'can_access_dashboard' },
    { text: 'Homepage Content', icon: <HomeContentIcon />, path: '/admin/home-content', perm: 'can_manage_settings' },
    { text: 'Posts', icon: <PostsIcon />, path: '/admin/posts', perm: 'can_manage_posts' },
    { text: 'Board Members', icon: <BoardIcon />, path: '/admin/board-members', perm: 'can_manage_board' },
    { text: 'Team Members', icon: <TeamIcon />, path: '/admin/team-members', perm: 'can_manage_team' },
    { text: 'Jobs', icon: <JobsIcon />, path: '/admin/jobs', perm: 'can_manage_jobs' },
    { text: 'Job Applications', icon: <ApplicationsIcon />, path: '/admin/job-applications', perm: 'can_view_job_applications' },
    { text: 'Contact Messages', icon: <ContactMessagesIcon />, path: '/admin/contact-messages', perm: 'can_view_contact_messages' },
    { text: 'Doctors', icon: <DoctorsIcon />, path: '/admin/doctors', perm: 'can_manage_doctors' },
    { text: 'Departments', icon: <DepartmentsIcon />, path: '/admin/departments', perm: 'can_manage_departments' },
    { text: 'Hospital Info', icon: <HospitalInfoIcon />, path: '/admin/hospital-info', perm: 'can_manage_settings' },
    { text: 'Services Pages', icon: <ServicesIcon />, path: '/admin/services-pages', perm: 'can_manage_settings' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings', perm: 'can_manage_settings' },
    { text: 'Access Control', icon: <SecurityIcon />, path: '/admin/access-control', perm: 'can_manage_users' },
  ];

  useEffect(() => {
    let mounted = true;
    const id = localStorage.getItem('currentUserId');
    if (!id) return;
    (async () => {
      try {
        const resp = await apiClient.getUserPermissions(id);
        if (mounted && resp?.success) {
          setPerms(resp.data);
          localStorage.setItem('currentUserPermissions', JSON.stringify(resp.data));
          
          // Check if user has access to dashboard
          if (location.pathname === '/admin') {
            if (!resp.data.can_access_dashboard) {
              // User doesn't have dashboard access, redirect to first available section
              const availableSections = menuItems.filter(mi => mi.perm && resp.data[mi.perm]);
              if (availableSections.length > 0) {
                console.log('User has no dashboard access, redirecting to first available section:', availableSections[0].path);
                navigate(availableSections[0].path);
              } else {
                console.log('User has no permissions at all, redirecting to no-access');
                navigate('/admin/no-access');
              }
            } else {
              console.log('User has dashboard access, staying on dashboard');
            }
          }
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, [currentUserId, navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLoginSuccess = (userData) => {
    setCurrentUser(userData);
    setCurrentUserId(userData.id);
    setPerms(userData);
    setIsAuthenticated(true);
    navigate('/admin');
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('currentUserId');
    localStorage.removeItem('currentUserPermissions');
    localStorage.removeItem('authToken');
    setCurrentUser(null);
    setCurrentUserId('');
    setPerms(null);
    setIsAuthenticated(false);
    navigate('/admin');
  };

  useEffect(() => {
    const onUnauthorized = () => handleLogout();
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  const handleNavigation = (path) => {
    console.log('Navigation requested to:', path);
    console.log('Current permissions:', perms);
    
    // Check if user has permission to access this route
    const menuItem = menuItems.find(mi => mi.path === path);
    if (menuItem && menuItem.perm && (!perms || !perms[menuItem.perm])) {
      // User doesn't have permission, show alert and stay on current page
      console.log('Permission denied for:', path);
      alert('You do not have permission to access this section.');
      return;
    } else {
      console.log('Permission granted for:', path);
      navigate(path);
    }
    setMobileOpen(false);
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Admin Panel
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.filter(mi => !mi.perm || (perms ? perms[mi.perm] : true)).map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={isActive}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  backgroundColor: isActive ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                  borderRight: isActive ? '3px solid #3b82f6' : '3px solid transparent',
                  '&:hover': {
                    backgroundColor: isActive ? 'rgba(59, 130, 246, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 130, 246, 0.2)',
                    },
                  },
                  '& .MuiListItemIcon-root': {
                    color: isActive ? '#3b82f6' : 'inherit',
                  },
                  '& .MuiListItemText-primary': {
                    color: isActive ? '#3b82f6' : 'inherit',
                    fontWeight: isActive ? 600 : 400,
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/')}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Back to Website" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  // Show login component if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Hearts International Hospital - Admin Dashboard - Welcome, {currentUser?.first_name || currentUser?.username}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8
        }}
      >
        <Container maxWidth="lg">
          {/* Permission Guard for Routes */}
          {(() => {
            const currentPath = location.pathname;
            const menuItem = menuItems.find(mi => mi.path === currentPath);
            
            // Check if user has permission for current route
            if (menuItem && menuItem.perm && (!perms || !perms[menuItem.perm])) {
              // User doesn't have permission, show access denied message
              return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h4" color="error" gutterBottom>
                    Access Denied
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    You do not have permission to access this section.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Please contact your administrator for access.
                  </Typography>
                </Box>
              );
            }
            
            // User has permission or route doesn't require permission
            return <Outlet />;
          })()}
        </Container>
      </Box>
    </Box>
  );
};

export default AdminLayout; 